import { authClient } from './amplifyClient';
import {
    createUser as createUserMutation,
    updateUser as updateUserMutation,
} from '../graphql/mutations';
import { getUser, usersByCognitoId } from '../graphql/queries';
import type { CompanyOwner, CreateUserInput, User } from '../types/schema';
import { DEFAULT_SUPER_ADMIN_PERMISSIONS, NETWORK_DOMAIN } from './constants';

type OwnerLike = Pick<
    CompanyOwner,
    'id' | 'email' | 'name' | 'cognitoId' | 'companyId' | 'role' | 'permissions' | 'domain'
> & {
    discrepancyNotification?: boolean | null;
    discrepancyNotificationTypes?: string | null;
};

function ownerPermissions(owner: OwnerLike): string {
    if (owner.permissions && owner.permissions.trim()) {
        return owner.permissions;
    }
    return JSON.stringify(DEFAULT_SUPER_ADMIN_PERMISSIONS.map((item) => item.value));
}

async function getUserById(id: string): Promise<User | null> {
    try {
        const result = (await authClient.graphql({
            query: getUser,
            variables: { id },
        })) as { data?: { getUser?: User | null } };
        return result.data?.getUser ?? null;
    } catch {
        return null;
    }
}

async function getUserByCognitoId(cognitoId: string): Promise<User | null> {
    try {
        const result = (await authClient.graphql({
            query: usersByCognitoId,
            variables: { cognitoId },
        })) as { data?: { usersByCognitoId?: { items?: (User | null)[] } } };
        const items = result.data?.usersByCognitoId?.items ?? [];
        return items.find((item): item is User => Boolean(item?.id)) ?? null;
    } catch {
        return null;
    }
}

/**
 * Ensures a real User row exists for a CompanyOwner (same id when creating).
 * Idempotent — used on login self-heal so owners get Daily Brief / user-scoped features.
 */
export async function ensureUserForCompanyOwner(owner: OwnerLike): Promise<User> {
    if (!owner?.id) {
        throw new Error('CompanyOwner id is required');
    }
    const email = owner.email?.trim().toLowerCase();
    const name = owner.name?.trim() || email;
    if (!email || !name) {
        throw new Error('CompanyOwner email and name are required to create a User');
    }

    let existing = await getUserById(owner.id);
    if (!existing && owner.cognitoId) {
        existing = await getUserByCognitoId(owner.cognitoId);
    }

    if (existing?.id) {
        const patch: { id: string; companyId?: string; cognitoId?: string } = { id: existing.id };
        if (!existing.companyId && owner.companyId) {
            patch.companyId = owner.companyId;
        }
        if (!existing.cognitoId && owner.cognitoId) {
            patch.cognitoId = owner.cognitoId;
        }
        if (patch.companyId || patch.cognitoId) {
            const updated = (await authClient.graphql({
                query: updateUserMutation,
                variables: { input: patch },
            })) as { data?: { updateUser?: User | null } };
            return { ...existing, ...(updated.data?.updateUser ?? {}), ...patch } as User;
        }
        return existing;
    }

    const input: CreateUserInput = {
        id: owner.id,
        email,
        name,
        cognitoId: owner.cognitoId || undefined,
        companyId: owner.companyId || undefined,
        role: owner.role || 'super-admin',
        permissions: ownerPermissions(owner),
        domain: owner.domain || NETWORK_DOMAIN,
        discrepancyNotification: owner.discrepancyNotification ?? true,
        discrepancyNotificationTypes:
            owner.discrepancyNotificationTypes || `{ "inop": true, "warn": true }`,
        safetyLessons: JSON.stringify([]),
        archived: false,
        globalLocationPermission: true,
    };

    try {
        const response = (await authClient.graphql({
            query: createUserMutation,
            variables: { input },
        })) as { data?: { createUser?: User | null } };

        const created = response.data?.createUser;
        if (!created?.id) {
            throw new Error('createUser returned no id for CompanyOwner');
        }
        return created;
    } catch (createErr) {
        // Race: another tab/call already created this User — re-read instead of failing login
        const fallback = await getUserById(owner.id);
        if (fallback?.id) return fallback;
        throw createErr;
    }
}
